import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@/services/documentApi';
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

interface FileWithPreview extends File {
  preview?: string;
  id: string;
}

interface FileUploadProgress {
  id: string;
  file: FileWithPreview;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  onUpload: (files: File[]) => Promise<void>;
  maxFiles?: number;
  minFiles?: number;
  maxSize?: number;
  /** MIME map for react-dropzone. Pass `null` to allow any file type. */
  accept?: Record<string, string[]> | null;
  disabled?: boolean;
  isUploading?: boolean;
  uploadProgress?: FileUploadProgress[];
}

const DEFAULT_ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/msword': ['.doc'],
  'image/heic': ['.heic'],
};

const DEFAULT_MAX_SIZE = 50 * 1024 * 1024; // 50MB  

export function FileUpload({
  onFilesSelected,
  onUpload,
  maxFiles = 60,
  minFiles = 1,
  maxSize = DEFAULT_MAX_SIZE,
  accept,
  disabled = false,
  isUploading = false,
  uploadProgress = [],
}: FileUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const dropzoneAccept = accept === null ? undefined : (accept ?? DEFAULT_ACCEPT);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach((rejection) => {
          const errors = rejection.errors.map((e) => e.message).join(', ');
          toast.error(`${rejection.file.name}: ${errors}`);
        });
      }

      // Check total file count
      const totalFiles = selectedFiles.length + acceptedFiles.length;
      if (totalFiles > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed. You have selected ${totalFiles} files.`);
        return;
      }

      // Add unique IDs to files
      const newFiles: FileWithPreview[] = acceptedFiles.map((file) => {
        const fileWithId = file as FileWithPreview;
        fileWithId.id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        return fileWithId;
      });

      const updatedFiles = [...selectedFiles, ...newFiles];
      setSelectedFiles(updatedFiles);
      onFilesSelected(updatedFiles);
    },
    [selectedFiles, maxFiles, onFilesSelected]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    maxSize,
    maxFiles: maxFiles - selectedFiles.length,
    disabled: disabled || isUploading,
  });

  const removeFile = (fileId: string) => {
    const updatedFiles = selectedFiles.filter((f) => f.id !== fileId);
    setSelectedFiles(updatedFiles);
    onFilesSelected(updatedFiles);
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    onFilesSelected([]);
  };

  const handleUpload = async () => {
    if (selectedFiles.length < minFiles) {
      toast.error(`Please select at least ${minFiles} file(s)`);
      return;
    }
    await onUpload(selectedFiles);
  };

  const getFileIcon = (file: FileWithPreview) => {
    const progress = uploadProgress.find((p) => p.file.id === file.id);
    if (!progress) return <FileText className="h-5 w-5 text-muted-foreground" />;

    switch (progress.status) {
      case 'uploading':
        return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      default:
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getFileProgress = (file: FileWithPreview) => {
    const progress = uploadProgress.find((p) => p.file.id === file.id);
    return progress?.progress || 0;
  };

  const isFileUploading = (file: FileWithPreview) => {
    const progress = uploadProgress.find((p) => p.file.id === file.id);
    return progress?.status === 'uploading';
  };

  return (
    <div className="space-y-4 min-w-0">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-4 sm:p-8 text-center cursor-pointer transition-colors',
          isDragActive && !isDragReject && 'border-primary bg-primary/5',
          isDragReject && 'border-destructive bg-destructive/5',
          !isDragActive && !isDragReject && 'border-muted-foreground/25 hover:border-primary/50',
          (disabled || isUploading) && 'opacity-50 cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full',
              isDragActive ? 'bg-primary/10' : 'bg-muted'
            )}
          >
            <Upload
              className={cn('h-7 w-7', isDragActive ? 'text-primary' : 'text-muted-foreground')}
            />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {isDragActive ? 'Drop your files here' : 'Drag & drop files here'}
            </p>
            <p className="text-xs text-muted-foreground">
              or click to browse from your computer
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            PDF, JPG, JPEG, PNG, HEIC, Word (DOC/DOCX), Excel up to {formatFileSize(maxSize)} each
          </p>
          <p className="text-xs text-muted-foreground">
            Minimum {minFiles} file, maximum {maxFiles} files
          </p>
        </div>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium min-w-0 truncate">
              Selected Files ({selectedFiles.length}/{maxFiles})
            </p>
            {!isUploading && (
              <Button variant="ghost" size="sm" className="shrink-0" onClick={clearAllFiles}>
                Clear all
              </Button>
            )}
          </div>

          <ScrollArea className="h-48 max-h-[50vh] sm:h-64 sm:max-h-none rounded-md border">
            <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
              {selectedFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-muted/50 min-w-0"
                >
                  <span className="shrink-0">{getFileIcon(file)}</span>
                  <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                    {isFileUploading(file) && (
                      <Progress value={getFileProgress(file)} className="h-1" />
                    )}
                  </div>
                  {!isUploading && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => removeFile(file.id)}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Remove file</span>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Upload Button */}
          <Button
            className="w-full"
            onClick={handleUpload}
            disabled={selectedFiles.length < minFiles || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
